import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { SongCard } from '@components/basics/SongCard';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { useAuth } from '@context/auth/base';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { LikedSoundsCardDetailsFragmentDoc } from '@graphql/reactQuery';
import { ShelfCreator } from '../ShelfCreator';
import { PlaylistReleaseCountText } from '../shared';
import { PreviewDropdown } from './Dropdown';
import { PlaylistCardSocialProof } from './PlaylistCardSocialProof';
import { usePlaylistCardInfo } from './usePlaylistCardInfo';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment LikedSoundsCardDetails on Shelf {
    id
    trackIds
    webappUri
    user {
      id
      ...ShelfCreator
    }
    ...PlaylistCardInfoHook
    ...PlaylistPreviewDropdownInfo
  }
`);

export const LikedSoundsCard = ({
  playlistFrag,
}: {
  playlistFrag: FragmentType<LikedSoundsCardDetailsFragmentDoc>;
}) => {
  const playlist = getFragment(LikedSoundsCardDetailsFragmentDoc, playlistFrag);
  const {
    isPlaying,
    onClick,
    isBufferingPlaylist,
    playlistPagePath,
    releaseCount,
    playlistName,
    type,
  } = usePlaylistCardInfo({
    data: playlist,
  });

  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const { authUser } = useAuth();

  const isOwner = authUser?.id === playlist.user.id;

  return (
    <SongCard
      isPlaying={isPlaying}
      loading={false}
      album={false}
      edition={false}
      expanded={null}
      setExpanded={null}
    >
      <SongCard.Cover
        coverLink={playlistPagePath}
        onPlayButtonClick={onClick}
        isBufferingSong={isBufferingPlaylist}
        image={<SongCard.Cover.Single src="/images/liked-sounds-cover-new.png" />}
      />

      <SongCard.Info
        header={<AccessibleLink pathnameUri={playlistPagePath}>{playlistName}</AccessibleLink>}
        subheader={<ShelfCreator data={playlist.user} showAvatar={false} />}
        actions={
          <div className="[&_path]:transition-colors [&_path]:duration-300 [&_path]:hover:fill-white">
            <PreviewDropdown
              playlist={playlist}
              trackIds={playlist.trackIds}
              isDropdownOpen={isDropdownOpen}
              setIsDropdownOpen={setIsDropdownOpen}
              isOwner={isOwner}
              playlistType={type}
              playlistLink={playlist.webappUri}
              variant="tertiary"
            />
          </div>
        }
        infoContainerBottom={<PlaylistReleaseCountText releaseCount={releaseCount} />}
        bottom={<PlaylistCardSocialProof playlistId={playlist.id} />}
      />
    </SongCard>
  );
};
